import type { Holiday } from '@/types';
import { holidaysApi } from '@/lib/api';
import { useOptimizedQuery } from './useOptimizedQuery';
import { useMemo } from 'react';

/**
 * Hook to fetch holidays for current and next year
 * Returns holidays for the next 12 months from today
 */
export function useHolidays() {
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  // Fetch current year holidays
  const currentYearQuery = useOptimizedQuery<Holiday[]>({
    queryKey: ['holidays', currentYear],
    queryFn: () => holidaysApi.getByYear(currentYear),
    tier: 'static',
  });

  // Fetch next year holidays
  const nextYearQuery = useOptimizedQuery<Holiday[]>({
    queryKey: ['holidays', nextYear],
    queryFn: () => holidaysApi.getByYear(nextYear),
    tier: 'static',
  });

  // Combine and filter for next 12 months
  const holidays = useMemo(() => {
    if (!currentYearQuery.data && !nextYearQuery.data) {
      return [];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const oneYearFromNow = new Date(today);
    oneYearFromNow.setFullYear(today.getFullYear() + 1);

    const allHolidays = [
      ...(currentYearQuery.data || []),
      ...(nextYearQuery.data || []),
    ];

    return allHolidays
      .filter((h) => {
        const holidayDate = new Date(h.date);
        return holidayDate >= today && holidayDate <= oneYearFromNow;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [currentYearQuery.data, nextYearQuery.data]);

  return {
    data: holidays,
    isLoading: currentYearQuery.isLoading || nextYearQuery.isLoading,
    isError: currentYearQuery.isError && nextYearQuery.isError,
    error: currentYearQuery.error || nextYearQuery.error,
  };
}

/**
 * Hook to get only the next upcoming holiday
 * Useful for the home page widget
 */
export function useNextHoliday() {
  const { data: holidays, isLoading, isError } = useHolidays();

  const nextHoliday = useMemo(() => {
    if (!holidays || holidays.length === 0) {
      return null;
    }
    return holidays[0];
  }, [holidays]);

  const daysUntil = useMemo(() => {
    if (!nextHoliday) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const holidayDate = new Date(nextHoliday.date);
    return Math.ceil(
      (holidayDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );
  }, [nextHoliday]);

  return {
    holiday: nextHoliday,
    daysUntil,
    isLoading,
    isError,
  };
}
